import { cn } from "~/lib/utils";
import React, { useEffect, useState } from "react";
import styles from "./styles.module.css";

// // // //

interface Props {
    tableRef: HTMLDivElement;
    isTablePaginated: boolean;
    onChange: (updatedVal: number) => void;
}

/**
 * HorizontalScrollbar
 * A custom horizontal scrollbar component that allows users to scroll horizontally using a range input
 * @param props.tableRef - The reference to the parent div that contains the content to be scrolled
 * @param props.isTablePaginated - A flag indicating whether the parent element is a paginated table
 * @param props.onChange - A callback function that is called every time the scrollbar value changes. It receives the updated value as an argument.
 */
export function HorizontalScrollbar(props: Props) {
    const { onChange, isTablePaginated, tableRef } = props;

    // Setting up state for percentScrolled
    const [percentScrolled, setPercentScrolled] = useState(0);

    // Setting up a ref for the input element
    const ref = React.useRef<HTMLInputElement>(null);

    // Setting up an effect for setting the percentScrolled state
    // when the scrollLeft or clientWidth on tableRef changes
    useEffect(() => {
        const scrollableWidth = tableRef.scrollWidth - tableRef.clientWidth;
        if (scrollableWidth <= 0) {
            setPercentScrolled(0);
            return;
        }
        setPercentScrolled((tableRef.scrollLeft / scrollableWidth) * 100);
    }, [tableRef.scrollLeft, tableRef.scrollWidth, tableRef.clientWidth]);

    // Handle the wheel event for the input element
    function handleWheel(event: WheelEvent) {
        // Prevent the default scroll behavior
        event.preventDefault();

        // Get the sign of the deltaX property of the event
        const delta = Math.sign(event.deltaX);

        // Calculate the percentage of the table that is currently scrolled
        const percentScrolledLatest =
            (tableRef.scrollLeft /
                (tableRef.scrollWidth - tableRef.clientWidth)) *
            100;

        // Calculate the number of percentage points to scroll by
        const deltaSteps = delta * 10;

        // Calculate the new percentage scrolled value, clamped between 0 and 100
        const newValue = Math.min(
            Math.max(percentScrolledLatest + deltaSteps, 0),
            100,
        );

        // Update the percentScrolled state with the new value
        setPercentScrolled(newValue);

        // Calculate the new scroll position based on the new percentage scrolled value
        const scrollPercentage = newValue / 100;
        onChange(
            scrollPercentage * (tableRef.scrollWidth - tableRef.clientWidth),
        );
    }

    // Setup handleWheel callback on the input element
    useEffect(() => {
        if (ref.current) {
            ref.current.addEventListener("wheel", handleWheel, {
                passive: false,
            });
        }
        return () => {
            ref.current?.removeEventListener("wheel", handleWheel);
        };
    }, [ref]);

    // Hide scrollbar if there's nothing to scroll
    if (tableRef.scrollWidth <= tableRef.clientWidth) {
        return null;
    }

    // Calculating the display width percentage and rounding to the nearest 10
    const scrollEnd = tableRef.scrollLeft + tableRef.clientWidth;
    const displayWidthPercentage =
        (scrollEnd / tableRef.scrollWidth) * 100 -
        (tableRef.scrollLeft / tableRef.scrollWidth) * 100;
    const displayWidthToNearest10th =
        Math.round(displayWidthPercentage / 10) * 10;

    // Return null if the entire table is being displayed
    if (displayWidthToNearest10th > 99) {
        return null;
    }

    return (
        <input
            ref={ref}
            type="range"
            className={cn(styles.customRange, "w-full touch-none", {
                "mt-4": isTablePaginated,
                "my-4": !isTablePaginated,
                [styles.customRange10]: displayWidthToNearest10th === 10,
                [styles.customRange20]: displayWidthToNearest10th === 20,
                [styles.customRange30]: displayWidthToNearest10th === 30,
                [styles.customRange40]: displayWidthToNearest10th === 40,
                [styles.customRange50]: displayWidthToNearest10th === 50,
                [styles.customRange60]: displayWidthToNearest10th === 60,
                [styles.customRange70]: displayWidthToNearest10th === 70,
                [styles.customRange80]: displayWidthToNearest10th === 80,
                [styles.customRange90]: displayWidthToNearest10th === 90,
            })}
            min={0}
            max={100}
            value={percentScrolled}
            onChange={(e) => {
                const val = Number(e.currentTarget.value);
                setPercentScrolled(val);

                const scrollPercentage = val / 100;
                onChange(
                    scrollPercentage *
                        (tableRef.scrollWidth - tableRef.clientWidth),
                );
            }}
        />
    );
}
